import { redirect } from "react-router";
import { createAuthLoader } from "@shared-ui/loaders/auth-loader";
import { tenantIdFromPathname } from "@shared-ui/utils/tenant";
import { ENV } from "@/config/env";
// PRODUCT_DEV-748: Minizo ビルドでは "minizo" になる。clearToken(utils/logout) と
// 必ず同じ値を使うこと（片方だけ違うとログアウトでトークンが消えない）。
import { TOKEN_STORE_NAME } from "@/config/variant";
import { apiGet } from "@shared-ui/utils/api";
import {
  API_PRESETS,
  API_DICTIONARIES,
  API_REALTIME_CONFIGS,
  API_ONP_PROMPT_SETTINGS,
  API_GLOBAL_DICTIONARY,
  API_MY_DEPARTMENT,
  AUTH_REFRESH,
} from "@/utils/const";
import type { WhoamiMe } from "@shared-domain/auth-service/contract";
import type {
  PresetsResponse,
  DictionariesResponse,
  RealtimeConfigsResponse,
  MyDepartmentResponse,
  DDBPreset,
  DDBDictionary,
  DDBRealtimeConfig,
} from "@shared-domain/unizo/contract";

/** PRODUCT_DEV-572: 自分の所属部署（未所属は null） */
export type MyDepartment = MyDepartmentResponse["department"];
import type { DDBONPPromptSetting, DDBGlobalDictionaryEntry } from "@shared-domain/admin/contract";
import type { ONPSettingsMap } from "@/lib/prompts/compose";

export interface RootLoaderData {
  /** 認証情報 */
  auth: WhoamiMe;
  /** プリセット一覧（Unizo 固有） */
  presets: DDBPreset[];
  /** 辞書マスタ（有効なもののみ） */
  dictionaries: DDBDictionary[];
  /** リアルタイムAPI設定（アクティブなもののみ） */
  realtimeConfigs: DDBRealtimeConfig[];
  /** ONPプロンプト設定（Admin管理、フォールバック用にundefined許容） */
  onpSettings?: ONPSettingsMap;
  /** グローバル辞書（Admin管理、テナント横断） */
  globalDictionary: DDBGlobalDictionaryEntry[];
  /** PRODUCT_DEV-572: 自分の所属部署（未所属/取得失敗は null = 絞り込みなし） */
  myDepartment: MyDepartment;
  /**
   * PRODUCT_DEV-721: **どこか1つでも** manager をしているか（業務軸）。
   * 部の集計を見せるかどうかの出し分けに使う。**認可はサーバ側が正**（改ざんは許容）。
   * 取得失敗時は false（見せない側に倒す）。
   */
  isManager: boolean;
}

/**
 * Root loader
 * 認証情報とグローバル設定を取得
 */
export async function rootLoader({
  request,
}: {
  request: Request;
}): Promise<RootLoaderData> {
  const url = new URL(request.url);

  // エラーページ（403/404）の場合はAPI呼び出しをスキップ
  // これにより、IP制限などで403が返る場合の無限リダイレクトを防ぐ
  if (url.pathname.includes("/403") || url.pathname.includes("/404")) {
    return {
      auth: {
        user: {
          id: "",
          name: "",
          email: "",
        },
        memberships: [],
        currentTenantId: "",
        info: undefined,
      },
      presets: [],
      dictionaries: [],
      realtimeConfigs: [],
      onpSettings: undefined,
      globalDictionary: [],
      myDepartment: null,
      // エラーページでは所属を引いていないので、見せない側へ倒す
      isManager: false,
    };
  }

  // Phase 1: 認証処理（共通ロジック）
  const authLoader = createAuthLoader({
    apiEndpoint: ENV.PRODUCT_API_ENDPOINT,
    authPortalUrl: ENV.AUTH_PORTAL_URL,
    productId: ENV.PRODUCT_ID,
    tokenStoreName: TOKEN_STORE_NAME,
    userPoolId: ENV.COGNITO_USERPOOL_ID,
    clientId: ENV.CLIENT_ID,
    authRefreshUrl: AUTH_REFRESH,
    // PRODUCT_DEV-641: 配信ホストでテナントが決まる環境だけ非空。
    // 空文字ではなく undefined を渡す（loader 側の分岐を「未指定」で素通しさせる）。
    fixedTenantId: ENV.FIXED_TENANT_ID || undefined,
  });

  const auth = await authLoader({ request });

  // PRODUCT_DEV-421: 認証済みユーザが /<tenantId>/ で来たら / にリダイレクト
  // （`/<tenantId>/` はエイリアスログイン専用入口のため、認証済なら通常 URL に戻す）
  // Unizo の React Router には `/:tenantId/*` ルートが無く、放置すると 404 にマッチするため明示的に処理する
  //
  // PRODUCT_DEV-641: 判定の正本は `@shared-ui/utils/tenant`。以前はここと
  // auth-loader に同じ正規表現が重複しており、片方だけ直すと
  // 「ポータルへは tenant 付きで飛ぶのに戻しが効かない」が静かに起きる形だった。
  if (tenantIdFromPathname(url.pathname)) {
    throw redirect("/");
  }

  // Phase 2: グローバル設定取得（Unizo 固有）
  // TODO: 認証成功後に tenantId を取得できるようにする
  // 現在は暫定で最初の membership の tenantId を使用
  const tenantId = auth.currentTenantId || auth.memberships[0]?.tenantId;

  // 並列取得 (404を許容するためallでなくallSettledを使用する)
  //
  // PRODUCT_DEV-652: companies / users はここから外し MasterDataProvider へ移した。
  // loader はナビゲーションをブロックするため、ここに残っている fetch の**最遅の 1 本**が
  // そのまま初回描画までの待ち時間になる。テナント規模に比例して伸びるのはこの 2 本だけで、
  // 残りはテナントによらずほぼ一定サイズ。詳細は contexts/MasterDataContext.tsx を参照。
  const [presetsResponse, dictionariesResponse, realtimeConfigsResponse, onpSettingsResponse, globalDictionaryResponse, myDepartmentResponse] =
    await Promise.allSettled([
      fetchPresets(tenantId),
      fetchDictionaries(tenantId),
      fetchRealtimeConfigs(tenantId),
      fetchONPSettings(tenantId),
      fetchGlobalDictionary(tenantId),
      fetchMyDepartment(tenantId),
    ]);

  return {
    auth,
    presets:
      presetsResponse.status === "fulfilled" ? presetsResponse.value : [],
    dictionaries:
      dictionariesResponse.status === "fulfilled" ? dictionariesResponse.value : [],
    realtimeConfigs:
      realtimeConfigsResponse.status === "fulfilled" ? realtimeConfigsResponse.value : [],
    onpSettings:
      onpSettingsResponse.status === "fulfilled" ? onpSettingsResponse.value : undefined,
    globalDictionary:
      globalDictionaryResponse.status === "fulfilled" ? globalDictionaryResponse.value : [],
    myDepartment:
      myDepartmentResponse.status === "fulfilled"
        ? myDepartmentResponse.value.department
        : null,
    isManager:
      myDepartmentResponse.status === "fulfilled"
        ? myDepartmentResponse.value.isManager
        : false,
  };
}

/**
 * PRODUCT_DEV-572: 自分の所属部署を取得（未所属/失敗は null = 絞り込みなし）
 */
async function fetchMyDepartment(
  tenantId: string,
): Promise<{ department: MyDepartment; isManager: boolean }> {
  // 他の fetch と同様に tenantId をクエリで渡す。
  // authMiddleware は getTenantIdFromRequest でリクエストの tenantId を必要とするため、
  // 付け忘れると "Missing tenantId in request" で認証解決に失敗し常に null になる（PRODUCT_DEV-572 バグ）。
  const params = new URLSearchParams({ tenantId });
  const response = await apiGet<MyDepartmentResponse>(
    `${API_MY_DEPARTMENT}?${params.toString()}`,
  );
  return response.success
    ? { department: response.department, isManager: response.isManager }
    : { department: null, isManager: false };
}

/**
 * プリセット一覧を取得
 */
async function fetchPresets(tenantId: string): Promise<DDBPreset[]> {
  const params = new URLSearchParams({ tenantId });
  const response = await apiGet<PresetsResponse>(
    `${API_PRESETS}?${params.toString()}`,
  );
  return response.success ? response.presets : [];
}

/**
 * 辞書マスター一覧を取得（有効なもののみ）
 */
async function fetchDictionaries(tenantId: string): Promise<DDBDictionary[]> {
  const params = new URLSearchParams({ tenantId });
  const response = await apiGet<DictionariesResponse>(
    `${API_DICTIONARIES}?${params.toString()}`,
  );
  return response.success ? response.dictionaries : [];
}

/**
 * リアルタイムAPI設定一覧を取得（アクティブなもののみ）
 */
async function fetchRealtimeConfigs(tenantId: string): Promise<DDBRealtimeConfig[]> {
  const params = new URLSearchParams({ tenantId });
  const response = await apiGet<RealtimeConfigsResponse>(
    `${API_REALTIME_CONFIGS}?${params.toString()}`,
  );
  return response.success ? response.realtimeConfigs : [];
}

/**
 * ONPプロンプト設定を取得（Admin管理テーブル）
 * AdminServiceStack-{env}-ONPPromptSettings テーブルから LAYER1/LAYER2 等を取得
 * 取得失敗時はundefinedを返し、ハードコードフォールバックに委譲
 */
async function fetchONPSettings(tenantId: string): Promise<ONPSettingsMap | undefined> {
  try {
    const params = new URLSearchParams({ tenantId });
    const response = await apiGet<{ settings: DDBONPPromptSetting[] }>(
      `${API_ONP_PROMPT_SETTINGS}?${params.toString()}`,
    );
    if (!response.settings || response.settings.length === 0) return undefined;
    const map: ONPSettingsMap = {};
    for (const s of response.settings) {
      map[s.settingId] = s;
    }
    return map;
  } catch {
    return undefined;
  }
}

/**
 * グローバル辞書を取得（Admin管理テーブル）
 * 取得失敗時は空配列を返す
 */
async function fetchGlobalDictionary(tenantId: string): Promise<DDBGlobalDictionaryEntry[]> {
  try {
    const params = new URLSearchParams({ tenantId });
    const response = await apiGet<{ entries: DDBGlobalDictionaryEntry[] }>(
      `${API_GLOBAL_DICTIONARY}?${params.toString()}`,
    );
    return response.entries || [];
  } catch {
    return [];
  }
}

// PRODUCT_DEV-652: fetchNotificationUnreadCount は削除した。
// 起動時に毎回 GET /notification/unread-count を叩いていたが、返り値 notificationUnreadCount を
// 読む画面が 1 つも無かった（未読バッジは useNewNotifications が独自ポーリングで算出している）。
